import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import Ajv from "ajv";
import AjvErrors from "ajv-errors";
import idx from "idx";
import _ from "lodash";
import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { updateProjectMaterials, bulkMaterialUpdate } from "src/actions/project-management.action";
import * as XLSX from "xlsx";
import {
  addBulkItem,
  checkMaterialUsage,
  listCategories,
  updateBulkItems,
} from "../../../../actions/inventory.action";
import {
  updateItemTemplateMaterials,
  updateTaskTemplateMaterials,
} from "../../../../actions/template-management.action";
import FileUplaoder from "../../../../components/file-uploader";
import CustomInput from "../../../../components/inputs/new-input";
import SelectInput from "../../../../components/inputs/select";
import CustomTable from "../../../../components/table/table";
import { CATALOGS, MATERIAL } from "../../../../constants/route-paths";
import { arrayToObject } from "../../../../util/common";
import { toUSD } from "../../../../util/currency-formatter";
import { showLoadingIndicator, hideLoadingIndicator, successSnackBar, showOverlay, hideOverlay } from "../../../../actions/common.action";
import { allowedExcelTypes } from "../../../../util/validators/file";

import { ImportSummaryDialog } from "./import-summary-dialog";
import { MaterialUsageDialog } from "./material-usage-dialog";
import { UpdateMaterialDialog } from "./update-material-cost-dialog";
import { ItemSchema } from "./validator";

const ajv = new Ajv({ allErrors: true });
AjvErrors(ajv, { singleError: true });
const defaults = {
  item: { key: "Item Description", label: "Item Description", items: [] },
  categoryId: { key: "Catalog", label: "Catalog", items: [], default: "" },
  manufacturer: {
    key: "Manufacturer",
    label: "Manufacturer",
    items: [],
    default: "",
  },
  partNo: { key: "Part No", label: "Part No", items: [] },
  cost: { key: "Cost", label: "Cost", items: [] },
  unit: { key: "UoM", label: "UoM", items: [], default: "" },
};

const validate = ajv.compile(ItemSchema);

function ImportMaterial() {
  const [file, setFile] = useState(null);
  const [data, setData] = useState([]);
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [mappers, setMappers] = useState(_.cloneDeep(defaults));
  const [status, setStatus] = useState({ total: 0, valid: 0, invalid: 0 });
  // const [updatedCosts, setUpdatedCosts] = useState(new Map())
  const catalogs = useSelector((state) => state.catalog.catalogs) || [];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [usageData, setUsageData] = useState(null);
  const [validatedItems, setValidatedItems] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({
    useItems: true,
    useItemTemplates: false,
  });
  const [showSummary, setShowSummary] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const catalogsMap = {
    ...arrayToObject(catalogs, "name", { lowerCase: true }),
    ...arrayToObject(catalogs, "id", { lowerCase: true }),
  };

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    if (catalogs.length < 1) await dispatch(listCategories());
  };

  const cancelHandler = () => {
    navigate(`/${CATALOGS}/${MATERIAL}`);
  };

  const parseXlsx = (file) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const workbook = XLSX.read(event.target.result, { type: "binary" });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const sheetData = XLSX.utils.sheet_to_json(sheet) || [];

      let _headers = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0];
      let _mappers = {};
      for (const key in defaults) {
        _mappers[key] = {
          ...defaults[key],
          key: _headers.includes(defaults[key].key)
            ? defaults[key].key
            : _headers.includes(`Generated ${defaults[key].key}`)
              ? `Generated ${defaults[key].key}`
              : "",
          items: _headers.map((item) => ({ key: item, value: item })),
        };
      }

      setMappers(_mappers);
      setData(sheetData);
      setStatus({ total: sheetData.length, valid: 0, invalid: 0 });
    };

    reader.readAsArrayBuffer(file);
  };

  const getMaterialsColumn = (mappers) => {
    return [
      {
        title: mappers.item.label,
        field: mappers.item.key,
        filtering: false,
      },
      {
        title: mappers.categoryId.label,
        field: mappers.categoryId.key,
        render: (data) =>
          idx(
            catalogsMap,
            (_) => _[(data[mappers.categoryId.key] || "").toLowerCase()].name
          ) ||
          idx(
            catalogsMap,
            (_) =>
              _[(idx(mappers, (_) => _.categoryId.default) || "").toLowerCase()]
                .name
          ),
      },
      {
        title: mappers.manufacturer.label,
        field:
          mappers.manufacturer.key ||
          idx(mappers, (_) => _.manufacturer.default) ||
          "",
        filtering: false,
        render: (data) =>
          data[mappers.manufacturer.key] ||
          idx(mappers, (_) => _.manufacturer.default),
      },
      {
        title: mappers.partNo.label,
        field: mappers.partNo.key,
        filtering: false,
      },
      {
        title: mappers.unit.label,
        field: mappers.unit.key || idx(mappers, (_) => _.unit.default),
        filtering: false,
        render: (data) =>
          data[mappers.unit.key] || idx(mappers, (_) => _.unit.default),
      },
      {
        title: mappers.cost.label,
        field: mappers.cost.key,
        filtering: false,
        class: "text-right",
        render: (data) =>
          toUSD(
            Number(
              (data[mappers.cost.key] || "")
                .toString()
                .trim()
                .replace(/[^0-9.-]+/g, "")
            )
          ),
      },
      {
        title: "Valid",
        field: "valid",
        filtering: false,
        class: "total-width text-right",
        render: (data) =>
          data["valid"] ? (
            <CheckIcon />
          ) : (
            <p>{idx(data, (_) => _["error"][0].message)}</p>
          ),
      },
      {
        title: "Action",
        field: "action",
        filtering: false,
        class: "text-center",
        render: (item) => (
          <div className="d-flex gap-2 justify-content-center">
            <DeleteIcon
              className="deleteicon"
              onClick={() => {
                let _temp = _.cloneDeep(data);
                _.remove(_temp, item);
                setData(_temp);
              }}
            />
          </div>
        ),
      },
    ];
  };

  const validateData = (isSubmit = false) => {
    let formatted = {},
      _temp = [],
      valid = false,
      count = { total: 0, valid: 0, invalid: 0 };
    let _data = data.map((item) => {
      formatted = {
        categoryId:
          idx(
            catalogsMap,
            (_) => _[item[mappers.categoryId.key].toLowerCase()].id
          ) || mappers.categoryId.default,
        item: item[mappers.item.key],
        cost: Number(
          (item[mappers.cost.key] || "")
            .toString()
            .trim()
            .replace(/[^0-9.-]+/g, "")
        ),
        manufacturer:
          item[mappers.manufacturer.key] || mappers.manufacturer.default,
        partNo: (item[mappers.partNo.key] || "").toString(),
        unit: item[mappers.unit.key] || mappers.unit.default,
      };
      valid = validate(formatted);
      count.total++;
      if (!valid) {
        count.invalid++;
        item["error"] = validate.errors;
        item["valid"] = false;
      } else {
        if (isSubmit) _temp.push(formatted);
        count.valid++;
        item["valid"] = true;
      }
      return item;
    });

    if (!_.isEqual(status, count)) setStatus(count);
    if (!isSubmit) setData(_data);
    return { count, items: _temp };
  };

  const downloadEdit = () => {
    let formatted = {},
      _temp = { invalid: [], valid: [] },
      valid = false;
    data.map((item) => {
      formatted = {
        ...item,
        [`Generated ${defaults.categoryId.label}`]:
          idx(
            catalogsMap,
            (_) => _[(item[mappers.categoryId.key] || "").toLowerCase()].name
          ) ||
          idx(
            catalogsMap,
            (_) =>
              _[(idx(mappers, (_) => _.categoryId.default) || "").toLowerCase()]
                .name
          ),
        [`Generated ${defaults.item.label}`]: item[mappers.item.key],
        [`Generated ${defaults.cost.label}`]: Number(
          (item[mappers.cost.key] || "")
            .toString()
            .trim()
            .replace(/[^0-9.-]+/g, "")
        ),
        [`Generated ${defaults.manufacturer.label}`]:
          item[mappers.manufacturer.key] || mappers.manufacturer.default,
        [`Generated ${defaults.partNo.label}`]: item[mappers.partNo.key],
        [`Generated ${defaults.unit.label}`]:
          item[mappers.unit.key] || mappers.unit.default,
      };
      delete formatted["error"];
      delete formatted["valid"];
      valid = validate({
        categoryId:
          idx(
            catalogsMap,
            (_) => _[item[mappers.categoryId.key].toLowerCase()].id
          ) || mappers.categoryId.default,
        item: (item[mappers.item.key] || "").toString(),
        cost: Number(
          (item[mappers.cost.key] || "")
            .toString()
            .trim()
            .replace(/[^0-9.-]+/g, "")
        ),
        manufacturer:
          item[mappers.manufacturer.key] || mappers.manufacturer.default,
        partNo: (item[mappers.partNo.key] || "").toString(),
        unit: item[mappers.unit.key] || mappers.unit.default,
      });
      formatted["error"] = (validate.errors || [])
        .map((error) => error.message)
        .join(", \n");
      valid ? _temp.valid.push(formatted) : _temp.invalid.push(formatted);
    });
    let filename = file.name.split(".");
    filename.pop();
    filename += " - Validated.xlsx";

    var ws = XLSX.utils.json_to_sheet([..._temp.invalid, ..._temp.valid]);
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Items");
    XLSX.writeFile(wb, filename);
  };

  const downloadTemplate = () => {
    let _data = [
      {
        [defaults.categoryId.label]: "",
        [defaults.item.label]: "",
        [defaults.cost.label]: 0,
        [defaults.manufacturer.label]: "",
        [defaults.partNo.label]: "",
        [defaults.unit.label]: "",
      },
    ];
    let filename = "Material Catalog Items Template.xlsx";
    var ws = XLSX.utils.json_to_sheet(_data);
    var wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample");
    XLSX.writeFile(wb, filename);
  };

  const importData = async (ignore = false) => {
    let { count, items } = validateData(true);
    items = _.uniqBy(items, "partNo");

    if (
      (count.total == count.valid || (count.valid > 0 && ignore)) &&
      count.total > 0
    ) {
      try {
        // Check all items for existing usage
        dispatch(
          checkMaterialUsage(
            items.map((item) => ({
              partNo: item.partNo,
              manufacturer: item.manufacturer,
              item: item.item,
            })),
            {
              useItems: true,
              useItemTemplates: true,
              useTaskTemplates: true
            }
          )
        )
          .then((usage) => {
            if (usage?.items?.length > 0 || usage?.itemTemplates?.length > 0 || usage?.taskTemplates?.length > 0) {
              setUsageData(usage);
              setValidatedItems(items);
              return;
            }
            // If no usage found, proceed with import
            proceedWithImport(items, selectedOptions);
          })
          .catch((error) => {
            console.error("Error checking material usage:", error);
          });
      } catch (error) {
        console.error("Error in import process:", error);
      }
    }
  };

  const handleUsageConfirm = (action, options) => {
    const cachedUsageData = usageData;
    setUsageData(null);
    if (action === "cancel") return;

    // Pass the updatedItems from usageData to proceedWithImport
    proceedWithImport(validatedItems, action, {
      ...options,
      usageData: cachedUsageData
    });
  };

  const proceedWithImport = async (
    items,
    action = "normal",
    options = {
      useItems: true,
      useItemTemplates: false,
      useProjects: false,
      useTaskTemplates: false,
      updatedItems: [],
    }
  ) => {
    dispatch(showOverlay());
    try {
      const summary = {
        updatedItems: [],
        newItems: [],
        updatedTemplates: [],
        skippedTemplates: [],
        updatedProjects: [],
        skippedProjects: [],
        updatedTaskTemplates: [],
        skippedTaskTemplates: [],
        warnings: [],
      };

      const {
        updatedItems,
        useItems,
        useItemTemplates,
        useProjects,
        useTaskTemplates,
        usageData: importUsageData
      } = options;

      const promises = [];
      const apiConfigs = { loader: false, successMessage: null };

      // Find items that need to be updated (have different costs)
      const itemsToUpdate =
        updatedItems?.filter((item) => {
          const importedItem = items.find((imp) => 
            String(imp.partNo || "").trim().toUpperCase() === String(item.part_no || "").trim().toUpperCase()
          );
          if (!importedItem) return false;
          
          const cleanCost = (val) => parseFloat(String(val || "0").replace(/[^0-9.]/g, ""));
          
          const importedCost = cleanCost(importedItem.cost);
          const currentCost = cleanCost(item.cost);
          
          const isDifferent = importedCost !== currentCost;
          return isDifferent;
        }) || [];

      // Handle inventory items if enabled
      if (useItems && itemsToUpdate.length > 0) {
        promises.push(
          dispatch(
            updateBulkItems({
              data: itemsToUpdate.map((item) => {
                const importedItem = items.find((imp) => 
                  String(imp.partNo || "").trim().toUpperCase() === String(item.part_no || "").trim().toUpperCase()
                );
                return {
                  id: item.id,
                  partNo: item.part_no,
                  item: item.item,
                  manufacturer: item.manufacturer,
                  itemDescription: item.item_description,
                  cost: importedItem.cost,
                  unit: item.unit,
                  categoryId: item.category_id,
                  saleAmount: parseFloat(item.sale_amount || 0),
                  saleAmountLock: item.sale_amount_lock,
                };
              }),
              approveAll: true,
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              summary.updatedItems = result.map((item) => ({
                id: item.id,
                item: item.item,
                part_no: item.part_no,
                cost: item.cost,
              }));
            }
          })
        );
      }

      // Handle item templates if selected
      if (useItemTemplates) {
        promises.push(
          dispatch(
            updateItemTemplateMaterials({
              items: items.map((item) => ({
                partNo: item.partNo,
                cost: item.cost,
                item: item.item,
                manufacturer: item.manufacturer,
                unit: item.unit
              })),
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              summary.updatedTemplates = [
                ...(summary.updatedTemplates || []),
                ...result.map((template) => ({
                  id: template.id,
                  name: template.name || template.description,
                }))
              ];
            }
          })
        );
      }

      // Handle projects if selected
      if (useProjects) {
        promises.push(
          dispatch(
            updateProjectMaterials({
              items: items.map((item) => ({
                partNo: item.partNo,
                cost: item.cost,
                item: item.item,
                manufacturer: item.manufacturer,
                unit: item.unit
              })),
              excludeProjectIds: options.selectedProjectIds || [],
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              const updatedList = Array.isArray(result) ? result : (result.updatedProjects || []);
              const skippedList = Array.isArray(result) ? [] : (result.skippedProjects || []);
              
              summary.updatedProjects = [
                ...(summary.updatedProjects || []),
                ...updatedList.map((project) => ({
                  id: project.id,
                  name: project.name,
                }))
              ];

              summary.skippedProjects = [
                ...(summary.skippedProjects || []),
                ...skippedList.map((project) => ({
                  id: project.id,
                  name: project.name,
                }))
              ];
            }
          })
        );
      }

      // Handle specific selected projects if any
      if (options.selectedProjectIds && options.selectedProjectIds.length > 0) {
        promises.push(
          dispatch(
            bulkMaterialUpdate({
              projectIds: options.selectedProjectIds,
              createRevision: true,
              items: items.map((item) => ({
                partNo: item.partNo,
                cost: item.cost,
                item: item.item,
                manufacturer: item.manufacturer,
                unit: item.unit
              })),
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              if (result.updatedProjects) {
                const newProjects = result.updatedProjects.map(p => ({
                  id: p.id,
                  name: p.name,
                  isNewRevision: true
                }));
                summary.updatedProjects = [...(summary.updatedProjects || []), ...newProjects];
              }
              if (result.warnings) {
                summary.warnings = [...(summary.warnings || []), ...result.warnings];
              }
            }
          })
        );
      }

      // Handle task templates if selected
      if (useTaskTemplates) {
        promises.push(
          dispatch(
            updateTaskTemplateMaterials({
              items: items.map((item) => ({
                partNo: item.partNo,
                cost: item.cost,
                item: item.item,
                manufacturer: item.manufacturer,
                unit: item.unit
              })),
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              summary.updatedTaskTemplates = [
                ...(summary.updatedTaskTemplates || []),
                ...result.map((template) => ({
                  id: template.id,
                  name: template.name,
                  updatedMaterials: template.updatedMaterialsCount || 0,
                }))
              ];
            }
          })
        );
      }

      // Add any new items that weren't in the existing items list
      const existingPartNos = new Set(updatedItems.map((item) => item.part_no));
      const newItemsList = items.filter(
        (item) => !existingPartNos.has(item.partNo)
      );

      if (newItemsList.length > 0) {
        promises.push(
          dispatch(
            addBulkItem({
              items: newItemsList,
            }, apiConfigs)
          ).then((result) => {
            if (result) {
              summary.newItems = result.map((item) => ({
                item: item.item,
                partNo: item.part_no,
                cost: item.cost,
              }));
            }
          })
        );
      }

      // Wait for all processes to complete
      await Promise.all(promises);

      // Populate skipped templates lists by comparing usageData with updated templates
      if (useItemTemplates && importUsageData?.itemTemplates) {
        const updatedIds = new Set(summary.updatedTemplates.map(t => t.id));
        summary.skippedTemplates = importUsageData.itemTemplates
          .filter(t => !updatedIds.has(t.id))
          .map(t => ({
            id: t.id,
            name: t.name || t.description
          }));
      } else {
        summary.skippedTemplates = [];
      }

      if (useTaskTemplates && importUsageData?.taskTemplates) {
        const updatedIds = new Set(summary.updatedTaskTemplates.map(t => t.id));
        summary.skippedTaskTemplates = importUsageData.taskTemplates
          .filter(t => !updatedIds.has(t.id))
          .map(t => ({
            id: t.id,
            name: t.name
          }));
      } else {
        summary.skippedTaskTemplates = [];
      }

      // Show summary dialog
      dispatch(successSnackBar("Materials processed successfully"));
      setImportSummary(summary);
      setShowSummary(true);
    } catch (error) {
      console.error("Error in import process:", error);
    } finally {
      dispatch(hideOverlay());
    }
  };


  return (
    <div className="purchase-order-block">
      <div className="row">
        <div className="w-auto">
          <h2 className="content-heading text-uppercase">Import Materials</h2>
          <div className="col-12 breadcrumb-block p-0">
            <Breadcrumb>
              <Breadcrumb.Item onClick={cancelHandler}>
                Materials
              </Breadcrumb.Item>
              <Breadcrumb.Item active>Import Materials</Breadcrumb.Item>
            </Breadcrumb>
          </div>
        </div>
        <div className="w-auto ms-auto my-auto">
          <div className="d-flex gap-3">
            <button
              className="secondarybtn btn-ch btn btn-primary"
              onClick={downloadTemplate}
            >
              Download Template
            </button>
            {data.length > 0 && (
              <button
                className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                onClick={() => validateData(false)}
              >
                Validate
              </button>
            )}
            {Number(status?.total) == Number(status?.valid) &&
              status?.total > 0 && (
                <button
                  className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                  onClick={importData}
                >
                  Import
                </button>
              )}
            {Number(status?.valid) > 0 &&
              Number(status?.total) !== Number(status?.valid) && (
                <button
                  className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                  onClick={() => importData(true)}
                >
                  Ignore & Import
                </button>
              )}
          </div>
        </div>
      </div>

      {catalogs.length > 0 ? (
        <div className="row mt-2 mb-1">
          <FileUplaoder
            title={"Upload Material Catalog Items"}
            files={file ? [file] : []}
            fileHandler={(files) => {
              setFile(files[0]);
              parseXlsx(files[0]);
            }}
            acceptedFileTypes={allowedExcelTypes}
          // acceptedFileTypes={[...allowedExcelTypes, ...allowedXMLTypes]}
          />
        </div>
      ) : (
        <div className="row mt-2 mb-1">
          <p>
            Sorry, you can't import the Materials. Because we are not able to
            find any Catalog, Please create some Catalogs.{" "}
          </p>
        </div>
      )}

      {data.length > 0 && (
        <>
          <div className="row mt-2 mb-1">
            <p className="m-0 text-decoration-underline text-left fw-medium">
              Fields Mapping
            </p>
            {Object.entries(mappers).map((item) => {
              let key = item[0],
                value = item[1];
              return (
                <div key={key} className="col-lg-4 col-md-6">
                  <SelectInput
                    placeholder={`Select ${value.label} Field`}
                    label={value.label}
                    items={value.items}
                    value={value.key}
                    onChange={(e) =>
                      setMappers((prev) => ({
                        ...prev,
                        [key]: {
                          ...prev[key],
                          key: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              );
            })}
          </div>

          <div className="row mt-2 mb-1">
            <div className="d-flex justify-content-between">
              <p className="m-0 text-decoration-underline text-left fw-medium">
                Defaults
              </p>
              <span className="small text-secondary">
                (If any values missing from the file, these defaults will be
                used to fill the empty values)
              </span>
            </div>

            <div className="col-lg-4 col-md-6">
              <SelectInput
                placeholder={`Select Default Catalog`}
                label={"Select Default Catalog"}
                items={catalogs.map((item) => ({
                  key: item.name,
                  value: item.id,
                }))}
                value={mappers.categoryId.default}
                onChange={(e) =>
                  setMappers((prev) => ({
                    ...prev,
                    categoryId: {
                      ...prev.categoryId,
                      default: e.target.value,
                    },
                  }))
                }
              />
            </div>
            <div className="col-lg-4 col-md-6">
              <CustomInput
                placeholder={`Default Manufacturer`}
                label={"Default Manufacturer"}
                value={mappers.manufacturer.default}
                onChange={(e) =>
                  setMappers((prev) => ({
                    ...prev,
                    manufacturer: {
                      ...prev.manufacturer,
                      default: e.target.value,
                    },
                  }))
                }
              />
            </div>
            <div className="col-lg-4 col-md-6">
              <CustomInput
                placeholder={`Default UoM`}
                label={"Default UoM"}
                value={mappers.unit.default}
                onChange={(e) =>
                  setMappers((prev) => ({
                    ...prev,
                    unit: {
                      ...prev.unit,
                      default: e.target.value,
                    },
                  }))
                }
              />
            </div>
          </div>

          <div className="d-flex justify-content-between mt-2">
            <span className="align-content-center text-decoration-underline text-left fw-medium">
              Data
            </span>
            <div className="d-flex gap-3">
              <span className="align-content-center text-primary">
                Total : {data?.length}
              </span>
              <span className="align-content-center text-success">
                Valid : {status?.valid}
              </span>
              <span className="align-content-center text-danger">
                Invalid : {status?.invalid}
              </span>
            </div>
            <div className="d-flex justify-content-between align-items-center gap-2">
              {Number(status?.valid) > 0 &&
                data.some((item) => item.isSelected) && (
                  <>
                    <div className="input-block search-block mt-0">
                      <input
                        type="text"
                        className="form-control-input"
                        placeholder="Search materials..."
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>
                    <Button
                      className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                      onClick={() => setShowUpdateDialog(true)}
                    >
                      Edit Cost ({data.filter((item) => item.isSelected).length}
                      )
                    </Button>
                  </>
                )}
              {Number(status?.total) > Number(status?.valid) && (
                <button
                  className="secondarybtn secondarybtn-outline btn-ch btn btn-primary"
                  onClick={downloadEdit}
                >
                  Download & Edit
                </button>
              )}
            </div>
          </div>
          <CustomTable
            toolbar={false}
            enableSearch={false}
            columns={getMaterialsColumn(mappers)}
            data={data.filter(
              (item) =>
                !search ||
                String(item[mappers.item.key] || "")
                  .toLowerCase()
                  .includes(search.toLowerCase()) ||
                String(item[mappers.partNo.key] || "")
                  .toLowerCase()
                  .includes(search.toLowerCase()) ||
                String(item[mappers.manufacturer.key] || "")
                  .toLowerCase()
                  .includes(search.toLowerCase())
            )}
            isEdit={true}
            editHandler={(item) => {
              // Clear other selections and select only this item
              const newData = data.map((d) => ({
                ...d,
                isSelected: d[mappers.item.key] === item[mappers.item.key],
              }));
              setData(newData);
              setShowUpdateDialog(true);
            }}
            fileName="materials"
          />
          {/* Update Dialog */}
          <UpdateMaterialDialog
            show={showUpdateDialog}
            onHide={() => setShowUpdateDialog(false)}
            onUpdate={(_materials) => {
              const newData = [...data];

              // Create map for O(1) lookup of original indices
              const indexMap = new Map(
                data.map((item, index) => [item[mappers.item.key], index])
              );

              // Update only the modified items using their original indices
              _materials.forEach((updatedItem) => {
                const index = indexMap.get(updatedItem[mappers.item.key]);
                if (index !== undefined) {
                  // Update the data
                  newData[index] = {
                    ...data[index],
                    [mappers.cost.key]: updatedItem.newCost,
                    isSelected: false,
                  };
                }
              });

              setData(newData);
              setShowUpdateDialog(false);
            }}
            materials={data.filter((item) => item.isSelected)}
            mappers={mappers}
          />
        </>
      )}

      {/* Add Material Usage Dialog */}
      <MaterialUsageDialog
        show={!!usageData}
        onHide={() => setUsageData(null)}
        usageData={usageData}
        importedItems={validatedItems}
        onConfirm={handleUsageConfirm}
      />

      {/* Import Summary Dialog */}
      <ImportSummaryDialog
        show={showSummary}
        onHide={() => {
          setShowSummary(false);
          setImportSummary(null);
          cancelHandler();
        }}
        summary={importSummary}
      />
    </div>
  );
}

export default ImportMaterial;
